<script setup lang="ts">
import { computed } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'

// Table footer for a paged list: "Showing 1–10 of 24 items" and a numbered pager.
// Presentational — emits `change` with the requested page. Long runs collapse to
// first / window / last with ellipses so the pager never outgrows a phone-width
// card.
const props = withDefaults(
  defineProps<{
    page: number
    totalPages: number
    totalItems: number
    pageSize: number
    /** What a row is, for "Showing 1–10 of N …". */
    itemLabel?: string
    itemsLabel?: string
    /** Names the pager for screen readers, e.g. "Order pages". */
    ariaLabel?: string
  }>(),
  { itemLabel: 'item', itemsLabel: 'items', ariaLabel: 'Pages' },
)

const emit = defineEmits<{ (e: 'change', page: number): void }>()

const WINDOW_RADIUS = 1

const rangeLabel = computed(() => {
  if (props.totalItems === 0) return `No ${props.itemsLabel}`
  const first = (props.page - 1) * props.pageSize + 1
  const last = Math.min(props.page * props.pageSize, props.totalItems)
  const noun = props.totalItems === 1 ? props.itemLabel : props.itemsLabel
  return `Showing ${first}–${last} of ${props.totalItems} ${noun}`
})

type PagerItem = { kind: 'page'; value: number } | { kind: 'gap'; key: string }

const items = computed<PagerItem[]>(() => {
  const total = props.totalPages
  const pages = new Set<number>([1, total])
  for (let p = props.page - WINDOW_RADIUS; p <= props.page + WINDOW_RADIUS; p++) {
    if (p >= 1 && p <= total) pages.add(p)
  }
  const sorted = [...pages].sort((a, b) => a - b)
  const result: PagerItem[] = []
  sorted.forEach((value, index) => {
    const previous = sorted[index - 1]
    if (previous !== undefined && value - previous > 1) {
      result.push({ kind: 'gap', key: `gap-${previous}` })
    }
    result.push({ kind: 'page', value })
  })
  return result
})

const canPrev = computed(() => props.page > 1)
const canNext = computed(() => props.page < props.totalPages)

function go(target: number) {
  if (target < 1 || target > props.totalPages || target === props.page) return
  emit('change', target)
}
</script>

<template>
  <Box flex align="center" justify="between" gap="3" class="pagination">
    <Text type="body-sm" color="muted" aria-live="polite">{{ rangeLabel }}</Text>

    <Box
      v-if="totalPages > 1"
      is="nav"
      flex
      align="center"
      class="pagination__pager"
      :aria-label="ariaLabel"
    >
      <Button
        variant="outline"
        color="secondary"
        size="small"
        icon="chevron-left"
        class="pagination__btn"
        :disabled="!canPrev"
        aria-label="Previous page"
        @click="go(page - 1)"
      />

      <template v-for="item in items" :key="item.kind === 'page' ? item.value : item.key">
        <Text
          v-if="item.kind === 'gap'"
          is="span"
          type="body-sm"
          color="muted"
          align="center"
          class="pagination__gap"
        >
          …
        </Text>
        <Button
          v-else
          :label="String(item.value)"
          :variant="item.value === page ? 'solid' : 'outline'"
          :color="item.value === page ? 'primary' : 'secondary'"
          size="small"
          class="pagination__btn"
          :aria-current="item.value === page ? 'page' : undefined"
          :aria-label="`Page ${item.value}`"
          @click="go(item.value)"
        />
      </template>

      <Button
        variant="outline"
        color="secondary"
        size="small"
        icon="chevron-right"
        class="pagination__btn"
        :disabled="!canNext"
        aria-label="Next page"
        @click="go(page + 1)"
      />
    </Box>
  </Box>
</template>

<style scoped>
.pagination {
  flex-wrap: wrap;
  padding: 0.875rem 1.25rem;
  background: var(--p-surface-0);
}

.pagination__pager {
  gap: 0.375rem;
}

/* The kit Button owns colour, focus and disabled states; the pager only fixes
   each control to the design's square footprint. */
.pagination__btn {
  width: 36px;
  height: 36px;
  padding: 0;
}

.pagination__gap {
  min-width: 1.25rem;
}

/* Touch targets grow to 44px once the pager is thumb-driven. */
@media (max-width: 992px) {
  .pagination__btn {
    width: 44px;
    height: 44px;
  }
}

/* Phone width: the range text and pager each take a full line, pager centred. */
@media (max-width: 480px) {
  .pagination {
    flex-direction: column;
    align-items: center;
  }
}
</style>
